import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';

// 👇 TES NOMS
const NOMS_INITIAUX = {
    "33374483762": "MILLOTTE AURELIEN",
    "33374483765": "LE VAILLANT AYMERIC",
    "33374483340": "MILLOTE CATHERINE ",
    "33743366923": "Gaetan"
};

// --- CONFIGURATION API ---
// Remplace par le chemin réel où tu as créé le fichier de l'étape 1
const API_URL_QUEUES = "https://centrex.amservices.eu/api/dashboard/queues.php";
const API_URL_STATS = "https://centrex.amservices.eu/api/dashboard/stats.php";


// 1. Normalisation
const normalizeNumber = (num) => {
    if (!num) return "";
    let clean = num.toString().replace(/\D/g, '');
    if (clean.startsWith('0')) return '33' + clean.substring(1);
    return clean;
};

// 2. Affichage
const formatForDisplay = (num) => {
    if (!num) return "";
    let clean = num.toString();
    if (clean.startsWith('33')) return '0' + clean.substring(2);
    return clean;
};

export default function Queues() {
    const [queues, setQueues] = useState({});

    // 🏆 Gestion des compteurs ET des IDs déjà comptés (Synchronisés Serveur)
    const [stats, setStats] = useState({ counters: {}, ids: [] });

    const [nomsDesFiles, setNomsDesFiles] = useState(() => {
        const saved = localStorage.getItem("mon_annuaire_telephonique");
        return saved ? { ...NOMS_INITIAUX, ...JSON.parse(saved) } : NOMS_INITIAUX;
    });

    const COLORS = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#00bcd4'];

    // 🔄 1. CHARGEMENT INITIAL & RÉGULIER
    useEffect(() => {
        const load = async () => {
            const timeParam = `?_t=${Date.now()}`;

            try {
                // A. On charge les files d'attente
                const resQueues = await fetch(API_URL_QUEUES + timeParam);
                const dataQueues = await resQueues.json();
                setQueues(dataQueues || {});

                // B. On charge les statistiques stockées EN LIGNE
                const resStats = await fetch(API_URL_STATS + timeParam);
                const dataStats = await resStats.json();

                // Si le serveur renvoie des données valides, on met à jour
                if (dataStats && dataStats.counters) {
                    setStats(prev => {
                        // Petit check pour ne pas écraser s'il n'y a pas de changement visuel
                        if (JSON.stringify(prev) !== JSON.stringify(dataStats)) {
                            return dataStats;
                        }
                        return prev;
                    });
                }

                // C. Mise à jour des noms (inchangé)
                setNomsDesFiles(prevNoms => {
                    const updates = { ...prevNoms };
                    let hasChanges = false;
                    Object.entries(dataQueues || {}).forEach(([queueID, calls]) => {
                        const callWithInfo = calls.find(c => c.destination?.display_name);
                        if (callWithInfo?.destination?.display_name && updates[queueID] !== callWithInfo.destination.display_name) {
                            updates[queueID] = callWithInfo.destination.display_name;
                            hasChanges = true;
                        }
                    });
                    if (hasChanges) {
                        localStorage.setItem("mon_annuaire_telephonique", JSON.stringify(updates));
                        return updates;
                    }
                    return prevNoms;
                });

            } catch (e) {
                console.error("Erreur de chargement", e);
            }
        };

        load();
        const i = setInterval(load, 2000);
        return () => clearInterval(i);
    }, []);


    // 🧮 2. LOGIQUE INTELLIGENTE DES COMPTEURS (Sauvegarde Serveur)
    // 🧮 2. LOGIQUE INTELLIGENTE DES COMPTEURS (Avec Anti-Doublon)
    useEffect(() => {
        let newStats = {
            counters: { ...stats.counters },
            ids: [...(stats.ids || [])]
        };
        let hasNewData = false;

        // Ce Set va nous servir à ne pas compter deux fois le même numéro DANS LA MÊME BOUCLE
        const numbersProcessedInThisLoop = new Set();

        Object.entries(queues).forEach(([rawID, calls]) => {
            let nomFile = nomsDesFiles[rawID];
            if (!nomFile) {
                const normID = normalizeNumber(rawID);
                nomFile = nomsDesFiles[normID] || normID;
            }

            calls.forEach(call => {
                // 1. On récupère le numéro et on le normalise (pour que 07... == 337...)
                const rawNumber = call.source?.display_id || call.source?.phone;
                const uniqueNumber = normalizeNumber(rawNumber);

                // 2. Si on a déjà traité ce numéro dans cette boucle, on l'ignore (c'est le doublon fantôme)
                if (numbersProcessedInThisLoop.has(uniqueNumber)) {
                    return;
                }

                // 3. Condition de comptage : Connecté + Pas encore compté historiquement
                if (call.state === 'connected' && !newStats.ids.includes(call.call_id)) {

                    if (!newStats.counters[nomFile]) newStats.counters[nomFile] = 0;
                    newStats.counters[nomFile] += 1;

                    // On ajoute l'ID à l'historique pour ne plus le compter les prochaines fois
                    newStats.ids.push(call.call_id);

                    // On ajoute le numéro au filtre temporaire pour bloquer son doublon immédiat
                    numbersProcessedInThisLoop.add(uniqueNumber);

                    if (newStats.ids.length > 50) newStats.ids.shift();
                    hasNewData = true;
                }
            });
        });

        if (hasNewData) {
            setStats(newStats);
            fetch(API_URL_STATS, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newStats)
            }).catch(err => console.error("Impossible de sauvegarder les stats", err));
        }

    }, [queues]);

    // 🧹 3. FUSION ET NETTOYAGE (Code inchangé)
    const mergedQueues = {};
    Object.entries(queues).forEach(([rawID, calls]) => {
        const uniqueKey = normalizeNumber(rawID);
        if (!mergedQueues[uniqueKey]) {
            mergedQueues[uniqueKey] = { ids: [rawID], calls: calls || [] };
        } else {
            mergedQueues[uniqueKey].ids.push(rawID);
            mergedQueues[uniqueKey].calls = [...mergedQueues[uniqueKey].calls, ...(calls || [])];
        }
    });

    const chartData = Object.entries(mergedQueues).map(([uniqueKey, data]) => {
        const callsClean = data.calls.filter(c => ['trying', 'ringing', 'connected'].includes(c.state));
        const uniqueCallers = new Set(callsClean.map(c => normalizeNumber(c.source?.phone)));
        let name = uniqueKey;
        for (const originalID of data.ids) {
            if (nomsDesFiles[originalID]) {
                name = nomsDesFiles[originalID];
                break;
            }
        }
        return { name, count: uniqueCallers.size, fullId: uniqueKey };
    }).filter(d => d.count > 0);


    // --- RENDER ---
    return (
        <div className="queue-section" style={{ padding: '20px' }}>

            {/* 🏆 COMPTEURS SERVER-SIDE */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                {Object.entries(stats.counters).map(([nom, count]) => (
                    <div key={nom} style={{
                        background: 'white', padding: '10px 20px', borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderLeft: '5px solid #4caf50'
                    }}>
                        <span style={{ display: 'block', fontSize: '0.8rem', color: '#666' }}>{nom}</span>
                        <strong style={{ fontSize: '1.5rem', color: '#333' }}>{count}</strong> <small>décrochés</small>
                    </div>
                ))}

                {/* Bouton RESET GLOBAL */}
                {Object.keys(stats.counters).length > 0 && (
                    <button
                        onClick={() => {
                            if (window.confirm("⚠️ Attention : Cela va remettre à zéro les compteurs pour TOUT LE MONDE. Continuer ?")) {
                                const resetData = { counters: {}, ids: [] };
                                setStats(resetData);
                                fetch(API_URL_STATS, {
                                    method: 'POST',
                                    body: JSON.stringify(resetData)
                                });
                            }
                        }}
                        style={{ marginLeft: 'auto', padding: '8px 12px', background: '#ff5252', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        🗑️ Reset Global
                    </button>
                )}
            </div>

            {/* 📊 GRAPHIQUE */}
            {/* {chartData.length > 0 && (
                <div style={{ height: 200, marginBottom: '20px', background: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: '0 0 5px 0', fontSize: '1rem', color: '#444' }}>📊 Charge en cours</h3>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart layout="vertical" data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" hide />
                            <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12, fontWeight: 500 }} />
                            <Tooltip cursor={{ fill: 'transparent' }} />
                            <Bar dataKey="count" barSize={20} radius={[0, 4, 4, 0]}>
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )} */}

            {/* 🗂️ GRILLE DES FILES */}
            <div className="queue-grid">
                {Object.entries(mergedQueues).map(([uniqueKey, data]) => {
                    let titreFile = uniqueKey;
                    for (const originalID of data.ids) {
                        if (nomsDesFiles[originalID]) {
                            titreFile = nomsDesFiles[originalID];
                            break;
                        }
                    }
                    const uniqueCalls = Object.values(
                        data.calls
                            .filter(c => ['trying', 'ringing', 'connected'].includes(c.state))
                            .reduce((acc, call) => {
                                const rawNumber = call.source?.display_id || call.source?.phone || call.call_id;
                                const uniqueKeyForDedup = normalizeNumber(rawNumber);
                                acc[uniqueKeyForDedup] = call;
                                return acc;
                            }, {})
                    );

                    return (
                        <div className="queue-card" key={uniqueKey} style={{ display: 'flex', flexDirection: 'column', maxHeight: '350px' }}>
                            <h3>📂 {titreFile}</h3>
                            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '5px' }}>
                                {uniqueCalls.length === 0 ? (
                                    <p>Aucun appel en cours</p>
                                ) : (
                                    uniqueCalls.map(call => (
                                        <div className="queue-call" key={call.call_id}
                                            style={{
                                                borderLeft: call.state === 'connected' ? '4px solid green' : '4px solid orange',
                                                marginBottom: '10px'
                                            }}>
                                            <span>
                                                {call.state === 'connected' ? '🗣️ En ligne' : '📞 Sonne'} :
                                                <strong> {formatForDisplay(call.source?.display_id || call.source?.phone)}</strong>
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}